import { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";
import FuseLoading from "@fuse/core/FuseLoading";
import adminService from "@fuse/services/adminService";

const container = {
	show: {
		transition: {
			staggerChildren: 0.1,
		},
	},
};

const columns = [
	{ id: "name", label: "Product Name" },
	{ id: "planPrice", label: "Price" },
	{ id: "currency", label: "Currency" },
	{ id: "lookupKey", label: "Lookup Key" },
	{ id: "oneTime", label: "Type" },
	{ id: "priceId", label: "Stripe Price ID" },
];

function StripePricesTable() {
	const [plans, setPlans] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [syncing, setSyncing] = useState(false);
	const [error, setError] = useState("");
	const [productFilter, setProductFilter] = useState("");

	const fetchPlans = () => {
		setLoading(true);
		setError("");
		adminService
			.getStripePrices()
			.then((result: any) => {
				setPlans(result);
				setLoading(false);
			})
			.catch((err) => {
				setError("Failed to load plans");
				setLoading(false);
			});
	};

	const handleSync = () => {
		setSyncing(true);
		setError("");
		adminService
			.syncStripePrices()
			.then(() => {
				setSyncing(false);
				fetchPlans();
			})
			.catch(() => {
				setError("Failed to sync from Stripe");
				setSyncing(false);
			});
	};

	useEffect(() => {
		fetchPlans();
	}, []);

	const activePlans = (() => {
		const active = plans.filter((p) => p.isActive);
		const recurring = active.filter((p) => !p.oneTime);
		const oneTimeByProduct = new Map<string, any>();
		for (const p of active.filter((p) => p.oneTime)) {
			const existing = oneTimeByProduct.get(p.name);
			if (!existing || (p.planPrice ?? 0) > (existing.planPrice ?? 0)) {
				oneTimeByProduct.set(p.name, p);
			}
		}
		return [...recurring, ...oneTimeByProduct.values()];
	})();

	const productNames = [...new Set(activePlans.map((p) => p.name))].sort();
	const filteredPlans = productFilter
		? activePlans.filter((p) => p.name === productFilter)
		: activePlans;

	const lifetimePriceId = (() => {
		const oneTime = activePlans.filter((p) => p.oneTime);
		if (oneTime.length === 0) return null;
		return oneTime.reduce((max, p) => (p.planPrice ?? 0) > (max.planPrice ?? 0) ? p : max).priceId;
	})();

	const renderCellValue = (plan: any, columnId: string) => {
		const value = plan[columnId];
		if (columnId === "name" && plan.priceId === lifetimePriceId) {
			return (
				<span className="flex items-center gap-8">
					{value ?? "-"}
					<CheckCircleIcon color="success" fontSize="small" />
				</span>
			);
		}
		if (columnId === "oneTime") {
			return value ? "One-Time" : "Recurring";
		}
		if (columnId === "planPrice") {
			return value != null ? `$${Number(value).toFixed(2)}` : "-";
		}
		if (columnId === "currency") {
			return value ? value.toUpperCase() : "-";
		}
		return value ?? "-";
	};

	return (
		<>
			<div className="flex flex-wrap w-full items-center justify-between px-8 py-12 md:px-16 border-b">
				<Typography className="font-700 text-2xl">
					Stripe Prices
				</Typography>
				<div className="flex items-center gap-12">
					<FormControl size="small" sx={{ minWidth: 200 }}>
						<InputLabel>Product</InputLabel>
						<Select
							value={productFilter}
							label="Product"
							onChange={(e) => setProductFilter(e.target.value)}
						>
							<MenuItem value="">All Products</MenuItem>
							{productNames.map((name) => (
								<MenuItem key={name} value={name}>
									{name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSync}
						disabled={syncing || loading}
						startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : null}
					>
						{syncing ? "Syncing..." : "Sync from Stripe"}
					</Button>
				</div>
			</div>

			{error && (
				<div className="px-16 py-8">
					<Typography color="error">{error}</Typography>
				</div>
			)}

			<TableContainer component={Paper} sx={{ maxHeight: 580 }}>
				<Table stickyHeader aria-label="Stripe prices">
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell className="font-700 bg-white" key={column.id}>
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredPlans.map((plan, index) => (
							<TableRow key={plan.priceId || index}>
								{columns.map((column) => (
									<TableCell
										className="font-400 cursor-default"
										key={column.id}
									>
										{renderCellValue(plan, column.id)}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
				{loading && (
					<motion.div
						className="flex w-full gap-24 min-w-0 p-48"
						variants={container}
						initial="hidden"
						animate="show"
					>
						<FuseLoading />
					</motion.div>
				)}
				{!loading && filteredPlans.length === 0 && !error && (
					<motion.div
						className="flex w-full gap-24 min-w-0 p-24"
						variants={container}
						initial="hidden"
						animate="show"
					>
						No plans found
					</motion.div>
				)}
			</TableContainer>
		</>
	);
}

export default StripePricesTable;
